/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { FC, memo, useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

import type * as Type from '@/common/interface';
import {
  aiMerchantCategoryCardDefinitions,
  buildAIMerchantCategoryCardHTML,
} from '@/common/aiMerchantCategoryCard';
import { buildSoftwareCardHTML } from '@/common/softwareDownloadCard';
import Icon from '@/components/Icon';
import {
  useAIMerchantList,
  usePublicDownloadRecommendations,
} from '@/services';

import './index.scss';

interface Props {
  onInsert: (cardHTML: string) => void;
}

const SoftwareDownloadCardPicker: FC<Props> = ({ onInsert }) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedServiceType, setSelectedServiceType] =
    useState<Type.AIMerchantServiceType>('plus_recharge');
  const { data, isLoading } = usePublicDownloadRecommendations({
    page: 1,
    page_size: 50,
  });
  const plusMerchantList = useAIMerchantList({
    service_type: 'plus_recharge',
    page: 1,
    page_size: 1,
  });
  const codeMerchantList = useAIMerchantList({
    service_type: 'long_code',
    page: 1,
    page_size: 1,
  });
  const packages = data?.list || [];
  const selectedPackage = useMemo(
    () => packages.find((item) => item.id === selectedId),
    [packages, selectedId],
  );
  const merchantCategoryCards = useMemo(() => {
    const countMap: Record<string, number> = {
      plus_recharge: Number(plusMerchantList.data?.count || 0),
      long_code: Number(codeMerchantList.data?.count || 0),
    };

    return aiMerchantCategoryCardDefinitions.map((item) => ({
      ...item,
      count: countMap[item.serviceType] || 0,
    }));
  }, [codeMerchantList.data?.count, plusMerchantList.data?.count]);
  const selectedMerchantCategory = useMemo(
    () =>
      merchantCategoryCards.find(
        (item) => item.serviceType === selectedServiceType,
      ),
    [merchantCategoryCards, selectedServiceType],
  );

  useEffect(() => {
    if (!selectedId && packages.length > 0) {
      setSelectedId(packages[0].id);
    }
  }, [packages, selectedId]);

  return (
    <div className="software-card-picker">
      {(isLoading || packages.length > 0) && (
        <div className="software-card-picker-row">
          <Form.Select
            size="sm"
            aria-label="选择已上架软件"
            disabled={isLoading || packages.length === 0}
            value={selectedId || ''}
            onChange={(evt) => setSelectedId(Number(evt.currentTarget.value))}>
            {isLoading ? (
              <option value="">加载已上架软件...</option>
            ) : (
              packages.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.software_name}
                </option>
              ))
            )}
          </Form.Select>
          <Button
            type="button"
            size="sm"
            variant="outline-primary"
            disabled={!selectedPackage}
            onClick={() => {
              if (selectedPackage) {
                onInsert(buildSoftwareCardHTML(selectedPackage));
              }
            }}>
            <Icon name="plus-circle" className="me-1" />
            插入软件卡片
          </Button>
        </div>
      )}
      <div className="software-card-picker-row">
        <Form.Select
          size="sm"
          aria-label="选择AI服务商分类"
          value={selectedServiceType}
          onChange={(evt) =>
            setSelectedServiceType(
              evt.currentTarget.value as Type.AIMerchantServiceType,
            )
          }>
          {merchantCategoryCards.map((item) => (
            <option value={item.serviceType} key={item.serviceType}>
              {item.title}（{item.count}家）
            </option>
          ))}
        </Form.Select>
        <Button
          type="button"
          size="sm"
          variant="outline-primary"
          disabled={!selectedMerchantCategory}
          onClick={() => {
            if (selectedMerchantCategory) {
              onInsert(
                buildAIMerchantCategoryCardHTML(selectedMerchantCategory),
              );
            }
          }}>
          <Icon name="plus-circle" className="me-1" />
          插入服务商分类卡
        </Button>
      </div>
    </div>
  );
};

export default memo(SoftwareDownloadCardPicker);
